import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/react';
import { LocaleType, ScenarioUpdate } from '@/model';
import {
    isValidContextExcludeKeys, isValidMessageHistoryCustom,
    isValidConstants, isValidTitle, isValidFaq, isValidWebhookUrl
} from '@/util/helper/settingHelper';
import { useScenario, useUpdateScenario, useDeleteScenario } from '@/query/scenario';
import { useDialog, useRouteHandler, useScenarioId, useSyncForm } from '@/hook';
import Button from '@/component/Button';
import { errorToast, successToast } from '@/component/Toast';

import SettingDisclosure from './component/SettingDisclosure';
import {
    ScenarioBaseInputGroup, ChattingInputGroup, NLUInputGroup,
    FAQInputGroup, ConstantInputGroup, StatisticsInputGroup
} from './component/inputGroup';


type SettingTab = 'scenario' | 'nlu';

const tabs = [
    { id: 'scenario', labelIntl: 'b0014' },
    { id: 'nlu', labelIntl: 'i10001' }
] as const;

const SettingView: React.FC = () => {
    const intl = useIntl();
    const dialog = useDialog();
    const scenarioId = useScenarioId();
    const { moveToScenarioPage } = useRouteHandler();
    const { syncForm } = useSyncForm();

    const [activeTab, setActiveTab] = useState<SettingTab>('scenario');
    const [submitData, setSubmitData] = useState<ScenarioUpdate>();
    const [validation, setValidation] = useState<boolean>(false);
    const { data: scenario } = useScenario(scenarioId);

    const { mutateAsync: updateScenario, isPending: isSaving } = useUpdateScenario(scenarioId);
    const { mutateAsync: deleteScenario, isPending: isDeleting } = useDeleteScenario(scenarioId);

    const onClickSave = () => {
        if (!submitData || !validation) {
            return;
        }
        const uplodaData = {
            ...submitData,
            faq: submitData.faq?.map(item => ({
                category: item.category.trim(),
                qna_list: item.qna_list.filter(qna => qna.question.trim() && qna.answer.trim()),
            })),
            properties: {
                ...submitData.properties,
                message_history_custom: (submitData.properties?.message_history_custom ?? []).filter(item => item.key.trim() && item.label.trim()),
                context_exclude_keys: (submitData.properties?.context_exclude_keys ?? []).filter(item => item.trim()),
            },
        };
        updateScenario(uplodaData)
            .then(() => {
                successToast(intl.formatMessage({ id: "t003" }));
            }).catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    const onClickDelete = () => {
        dialog.open({
            title: intl.formatMessage({ id: "i10002" }),
            content: intl.formatMessage({ id: "i10003" }),
            variant: "danger",
            onConfirm: async () => {
                await deleteScenario()
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }));
                        moveToScenarioPage();
                    }).catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            }
        });
    };

    const defaultSubmitData = useMemo(() => {
        if (!scenario) {
            return undefined;
        }
        return {
            name: scenario?.name,
            description: scenario?.description,
            properties: structuredClone(scenario?.properties),
            faq: structuredClone(scenario?.faq),
            constants: structuredClone(scenario?.constants),
        };
    }, [scenario]);

    const resetData = useCallback((data?: ScenarioUpdate) => {
        if (!data) {
            return;
        }
        setSubmitData(data);
        setValidation(false);
        setTimeout(() => {
            syncForm();
        }, 100);
    }, [syncForm]);

    useEffect(() => {
        resetData(defaultSubmitData);
    }, [defaultSubmitData, resetData]);

    if (!scenario || !submitData) {
        return null;
    }
    return (
        <TabGroup as="main"
                  className="scenario-content-layout pt-0 w-fit h-full flex flex-col gap-4"
                  selectedIndex={tabs.findIndex(t => t.id === activeTab)}
                  onChange={index => {
                      setActiveTab(tabs[index].id);
                      resetData(defaultSubmitData);
                  }}
        >
            <TabList aria-label="시나리오 설정"
                     className="w-full flex items-center gap-2"
            >
                {tabs.map(tab => (
                    <Tab key={tab.id}
                         className="tab-btn text-black-500 font-[400] border-transparent aria-selected:text-chat42 aria-selected:font-[600] aria-selected:border-chat42"
                    >
                        {intl.formatMessage({ id: tab.labelIntl })}
                    </Tab>
                ))}
            </TabList>
            <div className="size-full min-w-fit min-h-0 flex flex-col gap-3">
                <TabPanels as="div"
                           className="scroll-gap-container overflow-y-scroll"
                >
                    <TabPanel>
                        <ScenarioTabSettingAccordion submitData={submitData}
                                                     onChange={(submitData, validation) => {
                                                         setSubmitData(submitData);
                                                         setValidation(validation);
                                                     }}
                        />
                    </TabPanel>
                    <TabPanel>
                        <div className="scenario-setting-container">
                            <div className="scenario-setting-card scenario-setting-card-body">
                                <NLUInputGroup properties={submitData.properties ?? {}}
                                               onChange={properties => {
                                                   setSubmitData({ ...submitData, properties });
                                                   setValidation(true);
                                               }}
                                />
                            </div>
                        </div>
                    </TabPanel>
                </TabPanels>

                <div className="scroll-gap-container-align flex items-center gap-2">
                    <Button size="sm"
                            variant="danger"
                            className="mr-auto"
                            disabled={isSaving || isDeleting}
                            onClick={onClickDelete}
                    >
                        {intl.formatMessage({ id: 'b0006' })}
                    </Button>
                    <Button size="sm"
                            variant="outline-primary"
                            disabled={isSaving || isDeleting}
                            onClick={() => resetData(defaultSubmitData)}
                    >
                        {intl.formatMessage({ id: 'b0042' })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isSaving || isDeleting || !validation}
                            onClick={onClickSave}
                    >
                        {intl.formatMessage({ id: 'b0007' })}
                    </Button>
                </div>
            </div>
        </TabGroup>
    );
};


type ScenarioTabOpenKey = 'base' | 'chatting' | 'statistics' | 'faq' | 'constant';

interface ScenarioTabSettingAccordionProps {
    submitData: ScenarioUpdate;
    onChange: (submitData: ScenarioUpdate, validation: boolean) => void;
}

const ScenarioTabSettingAccordion: React.FC<ScenarioTabSettingAccordionProps> = (props) => {
    const { submitData, onChange } = props;

    const intl = useIntl();

    const [accordionKey, setAccordionKey] = useState<ScenarioTabOpenKey | undefined>(undefined);

    const onClickDisclosure = (key: ScenarioTabOpenKey) => {
        setAccordionKey(prev => prev === key ? undefined : key);
    };

    const onChangeInputs = (submitData: ScenarioUpdate) => {
        const validationTitle = isValidTitle(submitData.name ?? "").isValid;
        const validationWebhookUrl = isValidWebhookUrl(submitData.properties?.webhook_url).isValid;
        const validationMessageHistoryCustom = isValidMessageHistoryCustom(submitData.properties?.message_history_custom).isValid;
        const validationContextExcludeKeys = isValidContextExcludeKeys(submitData.properties?.context_exclude_keys).isValid;
        const validationConstants = isValidConstants(submitData.constants).isValid;
        const validationFaq = isValidFaq(submitData.faq).isValid;

        const validation = validationTitle && validationWebhookUrl && validationMessageHistoryCustom && validationContextExcludeKeys && validationConstants && validationFaq;

        onChange(submitData, validation);
    };

    const onChangeBaseSetting = (scenario: { name?: string, description?: string, locale?: LocaleType }) => {
        const { name, description, locale } = scenario;
        const newSubmitData = structuredClone(submitData);
        if (name !== undefined) {
            newSubmitData.name = name;
        }
        if (description !== undefined) {
            newSubmitData.description = description;
        }
        if (locale !== undefined) {
            newSubmitData.properties = {
                ...newSubmitData.properties,
                language: locale,
            };
        }
        onChangeInputs(newSubmitData,);
    };

    useEffect(() => {
        setAccordionKey('base');
        return () => {
            setAccordionKey(undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="scenario-setting-container">
            <SettingDisclosure title={intl.formatMessage({ id: "b0030" })}
                               isOpen={accordionKey === 'base'}
                               onToggle={() => onClickDisclosure('base')}
            >
                <ScenarioBaseInputGroup name={submitData.name}
                                        description={submitData.description}
                                        locale={submitData.properties?.language as LocaleType}
                                        onChange={onChangeBaseSetting}
                />
            </SettingDisclosure>

            <SettingDisclosure title={intl.formatMessage({ id: "b0033" })}
                               isOpen={accordionKey === 'chatting'}
                               onToggle={() => onClickDisclosure('chatting')}
            >
                <ChattingInputGroup properties={submitData.properties ?? {}}
                                    onChange={properties => onChangeInputs({ ...submitData, properties })}
                />
            </SettingDisclosure>

            <SettingDisclosure title={intl.formatMessage({ id: "b0032" })}
                               isOpen={accordionKey === 'statistics'}
                               onToggle={() => onClickDisclosure('statistics')}
            >
                <StatisticsInputGroup properties={submitData.properties ?? {}}
                                      onChange={properties => onChangeInputs({ ...submitData, properties })}
                />
            </SettingDisclosure>

            <SettingDisclosure title={intl.formatMessage({ id: "b0041" })}
                               isOpen={accordionKey === 'constant'}
                               onToggle={() => onClickDisclosure('constant')}
            >
                <ConstantInputGroup constants={submitData.constants ?? {}}
                                    legend={intl.formatMessage({ id: "b0041" })}
                                    onChange={constants => onChangeInputs({ ...submitData, constants })}
                />
            </SettingDisclosure>

            <SettingDisclosure title={intl.formatMessage({ id: "b0040" })}
                               isOpen={accordionKey === 'faq'}
                               onToggle={() => onClickDisclosure('faq')}
            >
                <FAQInputGroup faq={submitData.faq ?? []}
                               onChange={faqCategories => onChangeInputs({ ...submitData, faq: faqCategories })}
                />
            </SettingDisclosure>
        </div>
    );
};


export default SettingView;
